import { useEffect, useMemo, useState, FocusEvent } from 'react';
import {
  IntegerInputControl,
  RealInputControl,
} from '../../../../../types/types';
import Input from '../../../../Input/Input';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { getFormattedFloat } from '../../../../../utils/getFormattedFloat';
import { getDefaultValue } from './utils/getDefaultValue';
import { getReplaceRegex } from './utils/getReplaceRegex';
import { keepSingleDecimalSeparator } from '../../../../../utils/forms/inputs/keepSingleDecimalSeparator';
import { getCursorPosition } from '../../../../../utils/forms/inputs/getCursorPosition';
import { getSignificantDigitCount } from '../../../../../utils/forms/inputs/getSignificantDigitCount';
import { hasTooManyDigits } from '../../../../../utils/forms/inputs/hasTooManyDigits';
import { shouldFormatFloatValue } from './utils/shouldFormatFloatValue';
import { parseNumericString } from './utils/parseNumericString';
import { validateNumericInput } from '../../../../../utils/managerUtils';
import type { GridInputBaseProps } from '../../types';
import { resolveDecorator } from '../../utils/resolveDecorator';

type NumberInputProps = Omit<
  GridInputBaseProps<number | string | undefined>,
  'onFocusIn' | 'onFocusOut'
> & {
  control: IntegerInputControl | RealInputControl;
  isFloat: boolean;
  isFormInput: boolean;
  onBlur?: (event?: FocusEvent<HTMLInputElement>) => void;
  onFocusIn?: (event?: FocusEvent<HTMLInputElement>) => void;
  onFocusOut?: (event?: FocusEvent<HTMLInputElement>) => void;
  hideErrorText?: boolean;
};

export const NumberInput = ({
  value,
  name,
  error,
  isFloat,
  disabled,
  control,
  isFormInput,
  isColumnFilter = false,
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  setError,
  onBlur,
  hideErrorText,
}: NumberInputProps) => {
  const [formattedValue, setFormattedValue] = useState<string>('');
  const { t } = useTranslate();
  const resolvedDecorator = resolveDecorator(control.decorator);

  const onInputChange = (
    inputValue: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const replaceRegex = getReplaceRegex(control, isFloat);
    const cursorPosition = getCursorPosition(event);

    const sanitized = isFloat
      ? keepSingleDecimalSeparator(inputValue, cursorPosition).replace(
          replaceRegex,
          ''
        )
      : inputValue.replace(replaceRegex, '').replace(/^(-?)0+(\d)/, '$1$2');

    const previousDigitCount = getSignificantDigitCount(
      isFloat ? formattedValue : String(value ?? '')
    );

    if (
      getSignificantDigitCount(sanitized) > previousDigitCount &&
      hasTooManyDigits(sanitized)
    ) {
      return;
    }

    if (isFloat) {
      const nextFormattedValue = shouldFormatFloatValue(sanitized)
        ? getFormattedFloat(sanitized)
        : sanitized.replace('.', ',');
      setFormattedValue(nextFormattedValue);
    }

    const separatorCleared = sanitized.replace(',', '.');
    const parsed = parseNumericString(separatorCleared, isFloat);
    // Incomplete tokens (e.g. "", "-") keep the raw string so the sign/in-progress
    // input still renders; commit a number only once it parses.
    const numericValue = Number.isNaN(parsed) ? separatorCleared : parsed;
    onChange(numericValue);
    onTouch?.(numericValue);
    setError(null);
  };

  const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (value !== null && value !== undefined) {
      const error = validateNumericInput(String(value), control, t);
      if (error) {
        setError(error);
      } else if (onBlur) {
        onBlur(event);
      }
    }
    onFocusOut?.(event);
  };

  useEffect(() => {
    // Invalid value after switching from 'between' matchmode
    const defaultValue = getDefaultValue(
      control,
      isFormInput || isColumnFilter
    );
    const callResetError = typeof value === 'string' && value.includes(',');
    const callOnChange = callResetError || value === null;
    if (callOnChange) {
      // If the value is a float, set formatted value
      if (isFloat) {
        const formattedValue = getFormattedFloat(defaultValue);
        setFormattedValue(formattedValue);
      }
      const parsedDefault = parseNumericString(defaultValue, isFloat);
      // Programmatic default — do not propagate to field-level handlers.
      onChange(Number.isNaN(parsedDefault) ? '' : parsedDefault, false);
      if (callResetError) {
        setError(null);
      }
    }
  }, []);

  useEffect(() => {
    if (!isFloat) return;
    // Value cleared (e.g. a handler reset the field to null) — the float input
    // renders from formattedValue, so without this it would keep the stale
    // formatted string instead of going empty.
    if (value === null) {
      setFormattedValue('');
      return;
    }
    const stringValue = String(value);
    if (
      stringValue.includes('.') ||
      stringValue.includes(',') ||
      stringValue.length > 0
    ) {
      if (shouldFormatFloatValue(stringValue)) {
        const formattedValue = getFormattedFloat(stringValue);
        setFormattedValue(formattedValue);
      } else {
        setFormattedValue(stringValue.replace('.', ','));
      }
    }
  }, [value]);

  const resolvedValue: string = useMemo(() => {
    if (isFloat) {
      return formattedValue;
    }
    if (value === null) {
      return '';
    }
    return String(value);
  }, [isFloat, formattedValue, value]);

  return (
    <Input
      className="string-filter-input"
      name={name}
      type="text"
      value={resolvedValue}
      error={error}
      disabled={disabled}
      onChange={onInputChange}
      onBlur={onInputBlur}
      onFocus={onFocusIn}
      hideErrorText={hideErrorText}
      decorator={resolvedDecorator}
    />
  );
};
